/**
 * TravailLivePreview
 * ─────────────────────────────────────────────────────────────────
 * Side card "estimation" pour les pages /particuliers/travaux/[slug].
 * Met en avant la promesse Effy : économies % concrètes + aide max
 * cumulable + coût indicatif + reste à charge mini.
 *
 * Tous les chiffres viennent de la config travaux-particuliers.ts
 * (savingsPercent, maxAidEur, avgCostEur, cumulableWith) — aucune
 * donnée inventée.
 */

import { motion } from 'framer-motion';
import { CheckCircle2, TrendingDown } from 'lucide-react';
import { CountUp } from '../motion/CountUp';
import { easeStripe } from '../../lib/motion';
import { CUMUL_TONE_CLASSES, type TravailStub } from '../../config/travaux-particuliers';
import { cn } from '../../lib/cn';

interface Props {
  travail: TravailStub;
}

export function TravailLivePreview({ travail }: Props) {
  const costAvg = Math.round((travail.avgCostEur.min + travail.avgCostEur.max) / 2);
  const resteACharge = Math.max(0, costAvg - travail.maxAidEur);
  const couverturePct = Math.min(100, Math.round((travail.maxAidEur / costAvg) * 100));

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.18, ease: easeStripe }}
      className="rounded-2xl border border-stone-200/70 bg-paper p-5 shadow-[0_1px_2px_rgba(10,10,10,.04)] ring-1 ring-inset ring-brand-200/60 md:p-6"
    >
      {/* ── Header ── */}
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
            Estimation type · {travail.shortLabel.replace(/^l[ae]?\s/i, '')}
          </p>
          <p className="mt-0.5 text-xs text-stone-500">Maison · ménage aux revenus modestes</p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700 ring-1 ring-inset ring-brand-200/50">
          <span className="h-1 w-1 rounded-full bg-brand-500 animate-glow-pulse" />
          {travail.ficheCode}
        </span>
      </div>

      {/* ── Promesse économies ── */}
      <div className="mt-5 rounded-xl border border-brand-200/40 bg-gradient-to-br from-brand-50/60 to-accent-50/30 p-4">
        <div className="flex items-center gap-1.5">
          <TrendingDown className="h-3.5 w-3.5 text-brand-700" strokeWidth={2} />
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-800">
            Promesse
          </p>
        </div>
        <p className="mt-1.5 font-display text-xl font-bold leading-tight text-ink md:text-2xl">
          <span className="bg-gradient-to-br from-brand-700 to-accent-600 bg-clip-text text-transparent">
            −<CountUp end={travail.savingsPercent} suffix="%" />
          </span>{' '}
          {travail.savingsLabel}
        </p>
        <p className="mt-1 text-[11.5px] leading-relaxed text-stone-600">
          {travail.comfortBenefit}
        </p>
      </div>

      {/* ── Aide max ── */}
      <div className="mt-5 flex items-baseline gap-2 font-display tracking-tight">
        <span className="text-3xl font-bold text-ink md:text-4xl">
          <CountUp end={travail.maxAidEur} suffix=" €" />
        </span>
        <span className="text-xs text-stone-500">d'aides cumulables max</span>
      </div>

      {/* ── Détails coût / RAC ── */}
      <div className="mt-5 space-y-1.5">
        <div className="flex items-center justify-between rounded-lg border border-stone-200/70 bg-stone-50/40 px-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs text-stone-700">Coût indicatif TTC</p>
            <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500">
              Pose comprise
            </p>
          </div>
          <span className="ml-2 font-mono text-xs font-semibold text-ink tabular-nums">
            {travail.avgCostEur.min.toLocaleString('fr-FR')} – {travail.avgCostEur.max.toLocaleString('fr-FR')} €
          </span>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-brand-200/40 bg-gradient-to-r from-brand-50/40 to-paper px-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs text-stone-700">Reste à charge estimé</p>
            <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-700">
              {couverturePct}% pris en charge
            </p>
          </div>
          <span className="ml-2 font-mono text-xs font-bold text-brand-700 tabular-nums">
            ≈ {resteACharge.toLocaleString('fr-FR')} €
          </span>
        </div>
      </div>

      {/* ── Chips aides cumulables ── */}
      <div className="mt-5 border-t border-stone-200/60 pt-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
          Aides cumulables
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {travail.cumulableWith.map((aide) => {
            const classes = CUMUL_TONE_CLASSES[aide.tone];
            return (
              <span
                key={aide.label}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                  classes.chip,
                )}
              >
                <CheckCircle2 className="h-2.5 w-2.5" strokeWidth={2.6} />
                {aide.label}
              </span>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}
